import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	getSystemAssetTypes,
	selectDataTypes,
	selectLayersNamesInDataType,
	selectAreDataTypesLoaded,
} from '../redux/assetGridSlice';
import { getSpatialLayers } from '../../../redux/risk/actions';
import {
	selectSelectedSystemType,
	selectSelectedAssetType,
	setSelectedSystemTypeAction,
	setSelectedAssetTypeAction,
} from '../../useFilters/filterSlice';
import {
	systemSchemaSelector,
	getSystemSchemaAction,
	assetSchemaSelector,
	getAssetSchemaAction,
} from '@innovyze/lib_asset_schema_store';

type SelectedLayerData = {
	dataType: string;
	layerName: string;
};

type SchemaState = {
	systemTypes: string[];
	assetTypes: string[] | undefined;
	systemSchema: Record<string, { displayName?: string }>;
	assetSchema: Record<string, { displayName?: string }>;
	loadSystemSchema: (system: string) => void;
	loadAssetSchema: (assetType: string) => void;
};

type SelectedLayerDataHookReturnType = [
	boolean,
	SelectedLayerData,
	(dataType: string) => void,
	(layerName: string) => void,
	SchemaState,
];

export function useGridViewManager(): SelectedLayerDataHookReturnType {
	const dispatch = useDispatch();

	const systemType = useSelector(selectSelectedSystemType) ?? '';
	const assetType = useSelector(selectSelectedAssetType) ?? '';
	const systemTypes = useSelector(selectDataTypes);
	const assetTypes = useSelector(state =>
		selectLayersNamesInDataType(state, systemType),
	);
	const areDataTypesLoaded = useSelector(selectAreDataTypesLoaded);
	const systemSchema = useSelector(systemSchemaSelector);
	const assetSchema = useSelector(assetSchemaSelector);

	// Auto-select the only available system type when there is exactly one
	useEffect(() => {
		if (areDataTypesLoaded && !systemType && systemTypes.length === 1) {
			dispatch(setSelectedSystemTypeAction(systemTypes[0]));
		}
	}, [areDataTypesLoaded, systemTypes]);

	const isViewSelected = Boolean(systemType && assetType);
	const selectedView = { dataType: systemType, layerName: assetType };

	const setDataType = (dataType: string) => {
		dispatch(setSelectedSystemTypeAction(dataType));
	};
	const setLayerName = (layerName: string) => {
		dispatch(setSelectedAssetTypeAction(layerName));
	};

	const loadSystemSchema = (system: string) =>
		dispatch(getSystemSchemaAction(system));
	const loadAssetSchema = (type: string) =>
		dispatch(getAssetSchemaAction({ assetType: type }));

	useEffect(() => {
		dispatch(getSystemAssetTypes());
	}, []);

	useEffect(() => {
		dispatch(getSpatialLayers());
	}, []);

	return [
		isViewSelected,
		selectedView,
		setDataType,
		setLayerName,
		{
			systemTypes,
			assetTypes,
			systemSchema,
			assetSchema,
			loadSystemSchema,
			loadAssetSchema,
		},
	];
}
